import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    TextField,
    InputAdornment,
    Button,
    Box,
    TableCell,
    IconButton,
    TablePagination,
    Checkbox,
    Snackbar,
    Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import axiosInstance from "../../../api/axiosInstance";
import { useDebounce } from "../../../utils/useDebounce";
import ConfirmationModal from "../../ConfirmationModel";
import { UserFormModal } from "../../Admin/Users/UserFormModal";

interface User {
    id: number;
    username: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    role: string;
    createdAt: string;
    updatedAt: string;
}

export const UsersTable: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState<string>("");
    const [totalPages, setTotalPages] = useState<number>(1);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Debounce search term (1s delay)
    const debouncedSearch = useDebounce(search, 1000);

    // Fetch users
    const refreshUsers = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get<User[]>(`/users`, {
                params: {
                    page: currentPage + 1,
                    limit: 10,
                    search: debouncedSearch
                }
            });
            
            // The data is directly in response.data
            if (Array.isArray(response.data)) {
                setUsers(response.data);
                // Since we don't have totalPages in response, we'll use array length for now
                setTotalPages(Math.ceil(response.data.length / 10));
            } else {
                console.error("Invalid response format:", response.data);
                setUsers([]);
                setTotalPages(1);
            }
        } catch (error) {
            console.error("Error fetching users", error);
            setUsers([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshUsers();
    }, [currentPage, debouncedSearch]);

    const handleEdit = (user: User) => {
        console.log('UsersTable - editing user:', user);
        setSelectedUser(user);
        setShowFormModal(true);
    };

    const handleDelete = (user: User) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedUser) return;
        try {
            await axiosInstance.delete(`/users/${selectedUser.id}`);
            refreshUsers();
        } catch (error) {
            console.error("Error deleting user", error);
        }
        setShowDeleteModal(false);
        setSelectedUser(null);
    };

    const handleSaveUser = async (userData: Partial<User>) => {
        try {
            if (selectedUser) {
                await axiosInstance.put(`/users/${selectedUser.id}`, userData);
            } else {
                await axiosInstance.post('/users', userData);
            }
            refreshUsers();
            setShowFormModal(false);
            setSelectedUser(null);
        } catch (error) {
            console.error("Error saving user", error);
        }
    };

    const handleSelectUser = (userId: number) => {
        setSelectedUserIds(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            }
            return [...prev, userId];
        });
    };

    const handleSelectAll = () => {
        if (selectedUserIds.length === users.length) {
            setSelectedUserIds([]);
        } else {
            setSelectedUserIds(users.map(user => user.id));
        }
    };


    return (
        <>
            <Typography variant="h4" sx={{ marginBottom: 2 }}>
                Users
            </Typography>

            {/* Search and Add Button */}
            <Box display="flex" alignItems="center" mb={2} gap={2}>
                <TextField
                    label="Search by Name or Email"
                    variant="outlined"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(0);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flexGrow: 1 }}
                />

                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setSelectedUser(null);
                        setShowFormModal(true);
                    }}
                >
                    Add User
                </Button>
            </Box>

            {/* Table */}
            {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < users.length}
                                        checked={users.length > 0 && selectedUserIds.length === users.length}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                                <TableCell><strong>Username</strong></TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Phone</strong></TableCell>
                                <TableCell><strong>Role</strong></TableCell>
                                <TableCell><strong>Created At</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users && users.length > 0 ? (
                                users.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedUserIds.includes(user.id)}
                                                onChange={() => handleSelectUser(user.id)}
                                                disabled={!user.email}
                                            />
                                        </TableCell>
                                        <TableCell>{user.username}</TableCell>
                                        <TableCell>
                                            {user.firstName && user.lastName 
                                                ? `${user.firstName} ${user.lastName}` 
                                                : user.firstName || user.lastName || '-'}
                                        </TableCell>
                                        <TableCell>{user.email || '-'}</TableCell>
                                        <TableCell>{user.phoneNumber || '-'}</TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleEdit(user)} color="primary">
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(user)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No users found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePagination
                component="div"
                count={totalPages * 10}
                page={currentPage}
                onPageChange={(_, newPage) => setCurrentPage(newPage)}
                rowsPerPage={10}
                onRowsPerPageChange={() => {}}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                message={`Are you sure you want to delete user ${selectedUser?.username}?`}
            />

            {/* Add/Edit User Modal */}
            <UserFormModal
                open={showFormModal}
                onClose={() => {
                    setShowFormModal(false);
                    setSelectedUser(null);
                }}
                onSave={handleSaveUser}
                user={selectedUser}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}; 